// From underscore.js
let eq;

let deepEq;
let isFunction;
let clone;
let cloneObj;
eq = (a, b, aStack, bStack) => {
  // Identical objects are equal. `0 === -0`, but they aren't identical.
  // See the [Harmony `egal` proposal](http://wiki.ecmascript.org/doku.php?id=harmony:egal).
  if (a === b) return a !== 0 || 1 / a === 1 / b;
  // A strict comparison is necessary because `null == undefined`.
  if (a == null || b == null) return a === b;
  // `NaN`s are equivalent, but non-reflexive.
  if (a !== a) return b !== b;
  // Exhaust primitive checks
  const type = typeof a;
  if (type !== 'function' && type !== 'object' && typeof b !== 'object') return false;
  return deepEq(a, b, aStack, bStack);
};

// Internal recursive comparison function for `isEqual`.
deepEq = (a, b, aStack, bStack) => {
  // Compare `[[Class]]` names.
  const className = Object.prototype.toString.call(a);
  if (className !== Object.prototype.toString.call(b)) return false;
  switch (className) {
    // Strings, numbers, regular expressions, dates, and booleans are compared by value.
    case '[object RegExp]':
    // RegExps are coerced to strings for comparison (Note: '' + /a/i === '/a/i')
    case '[object String]':
      // Primitives and their corresponding object wrappers are equivalent; thus, `"5"` is
      // equivalent to `new String("5")`.
      return `${a}` === `${b}`;
    case '[object Number]':
      // `NaN`s are equivalent, but non-reflexive.
      // Object(NaN) is equivalent to NaN.
      if (+a !== +a) return +b !== +b;
      // An `egal` comparison is performed for other numeric values.
      return +a === 0 ? 1 / +a === 1 / b : +a === +b;
    case '[object Date]':
    case '[object Boolean]':
      // Coerce dates and booleans to numeric primitive values. Dates are compared by their
      // millisecond representations. Note that invalid dates with millisecond representations
      // of `NaN` are not equivalent.
      return +a === +b;
  }

  const areArrays = className === '[object Array]';
  if (!areArrays) {
    if (typeof a !== 'object' || typeof b !== 'object') return false;

    // Objects with different constructors are not equivalent, but `Object`s or `Array`s
    // from different frames are.
    const aCtor = a.constructor;

    const bCtor = b.constructor;
    if (aCtor !== bCtor && !(isFunction(aCtor) && aCtor instanceof aCtor
      && isFunction(bCtor) && bCtor instanceof bCtor)
      && ('constructor' in a && 'constructor' in b)) {
      return false;
    }
  }
  // Assume equality for cyclic structures. The algorithm for detecting cyclic
  // structures is adapted from ES 5.1 section 15.12.3, abstract operation `JO`.

  // Initializing stack of traversed objects.
  // It's done here since we only need them for objects and arrays comparison.
  aStack = aStack || [];
  bStack = bStack || [];
  let { length } = aStack;
  while (length--) {
    // Linear search. Performance is inversely proportional to the number of
    // unique nested structures.
    if (aStack[length] === a) return bStack[length] === b;
  }

  // Add the first object to the stack of traversed objects.
  aStack.push(a);
  bStack.push(b);

  // Recursively compare objects and arrays.
  if (areArrays) {
    // Compare array lengths to determine if a deep comparison is necessary.
    length = a.length;
    if (length !== b.length) return false;
    // Deep compare the contents, ignoring non-numeric properties.
    while (length--) {
      if (!eq(a[length], b[length], aStack, bStack)) return false;
    }
  } else {
    // Deep compare objects.
    const keys = Object.keys(a);

    let key;
    length = keys.length;
    // Ensure that both objects contain the same number of properties before comparing deep equality.
    if (Object.keys(b).length !== length) return false;
    while (length--) {
      // Deep compare each member
      key = keys[length];
      if (!(b.hasOwnProperty(key) && eq(a[key], b[key], aStack, bStack))) return false;
    }
  }
  // Remove the first object from the stack of traversed objects.
  aStack.pop();
  bStack.pop();
  return true;
};

isFunction = (obj) => typeof obj === 'function' || false;

clone = (o, curr) => {
  for (const l in o) {
    if (o[l] instanceof Object) {
      curr[l] = cloneObj(o[l]);
    } else {
      curr[l] = o[l];
    }
  }
  return curr;
};

cloneObj = (obj) => (obj instanceof Array
  ? obj.slice().map((v) => cloneObj(v))
  : obj instanceof Object
    ? clone(obj, {})
    : obj);

const separator = /[_!]/;
const joiner = '_';

export const splitSearch = (search) => {
  if (search[0] === '?') {
    search = search.substr(1);
  }
  return indexParamsByName(search.split('&'));
};

export const flattenParams = (paramsByName) => {
  const flatParams = [];
  for (const paramName in paramsByName) {
    if (paramsByName[paramName] instanceof Array) {
      const params = paramsByName[paramName].sort();
      flatParams.push(`${paramName}=${params.join(joiner)}`);
    }
  }
  return flatParams;
};

export const flattenValues = (paramsByName) => {
  const flatParams = [];
  for (const paramName in paramsByName) {
    const params = paramsByName[paramName].sort();
    params.forEach((param) => {
      flatParams.push(`${paramName}=${param}`);
    });
  }
  return flatParams;
};

export const joinParams = (paramsByName) => {
  const joinedParams = {};
  for (const paramName in paramsByName) {
    const params = paramsByName[paramName].sort();
    joinedParams[paramName] = params.join(joiner);
  }
  return joinedParams;
};

export const indexParamsByName = (params) => {
  const paramsByName = {};
  params.forEach((param) => {
    const parts = param.split(/=/);
    const paramName = parts[0];
    const paramValues = parts.slice(1).join('=').split(separator);
    if (paramName) {
      paramsByName[paramName] = paramsByName[paramName] !== undefined
        ? paramsByName[paramName].concat(paramValues)
        : paramValues;
    }
  });
  return paramsByName;
};

export const isEqual = (a, b) => eq(a, b);

export const cloneObject = (obj) => cloneObj(obj);
